import { Phone, CalendarClock, FileText, ShieldCheck, Zap, Building2, Clock, Snowflake } from "lucide-react";
import { COMPANY } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSettings } from "@/contexts/SettingsContext";
import { Link } from "react-router-dom";
import { onCallClick, onTextClick } from "@/lib/dialer";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

export default function Hero() {
  const { t } = useLang();
  const { settings } = useSettings();
  const heroImg = settings.hero_image || DEFAULT_HERO;
  const badges = [
    { icon: ShieldCheck, label: t("hero.badges")[0] },
    { icon: Clock, label: t("hero.badges")[1] },
    { icon: Zap, label: t("hero.badges")[2] },
    { icon: Building2, label: t("hero.badges")[3] },
  ];
  return (
    <section className="relative overflow-hidden grain" data-testid="hero">
      <div className="absolute inset-0 -z-10">
        <img src={heroImg} alt="AIR-COLD HVAC service" className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/70 to-ink-900/10" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink-900" />
      </div>

      <div className="mx-auto max-w-7xl px-5 md:px-8 pt-10 md:pt-20 pb-12 md:pb-20">
        {/* Hero logo — large, centered, with glow + slogan */}
        <div className="mb-8 md:mb-10 flex flex-col items-center text-center" data-testid="hero-logo">
          {settings.logo ? (
            <div className="relative inline-block">
              <div className="absolute inset-0 -z-10 blur-3xl bg-ice-400/30 rounded-full scale-110" aria-hidden="true"/>
              <img
                src={settings.logo}
                alt="AIR-COLD HVAC logo"
                className="h-40 sm:h-48 md:h-60 lg:h-72 w-auto mx-auto drop-shadow-[0_18px_50px_rgba(0,168,232,0.55)] rounded-2xl"
              />
            </div>
          ) : (
            <div className="inline-flex items-center gap-4 rounded-3xl bg-ink-800/70 ring-1 ring-ice-400/30 backdrop-blur-md px-5 py-3" data-testid="hero-logo-fallback">
              <div className="size-14 md:size-16 rounded-2xl bg-ice-400/15 ring-1 ring-ice-400/40 grid place-items-center">
                <Snowflake className="size-7 md:size-8 text-ice-400"/>
              </div>
              <div className="leading-tight text-left">
                <div className="font-display font-black text-2xl md:text-3xl tracking-wide">AIR-COLD</div>
                <div className="text-[11px] md:text-xs uppercase tracking-[0.3em] text-silver-300">HVAC</div>
              </div>
            </div>
          )}
          <div className="mt-5 text-[11px] md:text-xs font-bold uppercase tracking-[0.32em] text-ice-400" data-testid="hero-slogan">
            FAST. RELIABLE REPAIRS &amp; INSTALLATIONS.
          </div>
        </div>

        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 ring-1 ring-ice-400/40 bg-ice-400/10 text-ice-400 text-xs font-semibold uppercase tracking-[0.18em] animate-fade-up" data-testid="hero-eyebrow">
            <span className="size-1.5 rounded-full bg-ice-400 animate-pulse" />
            San Gabriel Valley • Southern California
          </div>

          <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight animate-fade-up" data-testid="hero-headline">
            {t("hero.h1")}
          </h1>

          <p className="mt-4 text-lg md:text-xl text-silver-300 max-w-2xl animate-fade-up" data-testid="hero-subhead">
            {t("hero.sub")}
          </p>

          <div className="mt-6 flex flex-wrap gap-2.5 animate-fade-up">
            <a href={`tel:${COMPANY.phoneTel}`} onClick={onCallClick} className="btn-primary px-5 py-3.5 rounded-full text-sm font-semibold flex items-center gap-2" data-testid="hero-call">
              <Phone className="size-4" /> {t("cta.call")}
            </a>
            <a href={COMPANY.sms} onClick={onTextClick} className="btn-ghost px-5 py-3.5 rounded-full text-sm font-semibold flex items-center gap-2" data-testid="hero-text">
              <Phone className="size-4" /> {t("cta.text")}
            </a>
            <Link to="/schedule" className="btn-ghost px-5 py-3.5 rounded-full text-sm font-semibold flex items-center gap-2" data-testid="hero-schedule">
              <CalendarClock className="size-4" /> {t("cta.schedule")}
            </Link>
            <a href="#estimate" className="btn-ghost px-5 py-3.5 rounded-full text-sm font-semibold flex items-center gap-2" data-testid="hero-estimate">
              <FileText className="size-4" /> {t("cta.estimate")}
            </a>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="hero-badges">
            {badges.map(({ icon: Icon, label }, i) => (
              <div key={i} className="flex items-center gap-2.5 rounded-2xl bg-ink-800/70 ring-1 ring-white/10 px-3 py-2.5">
                <div className="size-9 rounded-xl bg-ice-400/15 ring-1 ring-ice-400/30 grid place-items-center">
                  <Icon className="size-4 text-ice-400" />
                </div>
                <span className="text-xs font-semibold text-silver-300">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
